// Decisions the ALL ALARMS screen makes, kept out of the component so they can
// be tested — `cloud/web`'s runner has no DOM, so a rule that needs a mount is a
// rule that never gets a test. The screen renders these answers; it does not
// re-derive them.
//
// Three of them carry honesty weight the redesign could easily have flattened:
//
//   - The status pill has FIVE outcomes, not three. The design draws
//     ACTIVE / QUIET / OFF; this product also ships "enabled but the boat is not
//     evaluating it" (compile-pending, or a runtime reason such as
//     `paused_by_owner`). Folding that into QUIET would promise cover that does
//     not exist, and folding it into OFF would send an owner to re-enable a
//     switch that is already on. It gets its own muted pill.
//   - The On/Off filter hides ROWS, so a group that empties under it disappears
//     — but its heading count and its status dot still describe the group, not
//     the filtered slice. A group whose only firing member is switched off must
//     not quietly lose its red dot the moment you filter to On.
//   - The mini-bar reads its value from the SAME window it scales against.
//     The live value line beside it is the authoritative present-tense claim;
//     the bar is a picture of the last six hours, and mixing a live instant into
//     a bucketed window would let the fill and the domain come from two clocks.

import type { AlarmSeverity, ConfiguredAlarmSection, ConfiguredAlarmView } from './alarmDisplay';
import { miniBarModel, type AlarmSpecies, type MiniBarModel } from './alarmRedesign';
import type { AlarmsFilter } from './alarmRedesign';
import { formatRelativeShort } from './deviceSeen';
import type { EvidenceSeries } from './useAlarmEvidence';

// ---------------------------------------------------------------------------
// The status pill.
// ---------------------------------------------------------------------------

/**
 * Which pill a row wears.
 *
 * `stalled` is the state the design has no word for: the definition is enabled,
 * the owner believes it is on, and the boat is not evaluating it. The row's own
 * chip says why (`describeRuntimeDisabledReason`); the pill only has to keep it
 * out of QUIET.
 */
export type AllRowPillKind = 'alarm' | 'warning' | 'caution' | 'quiet' | 'stalled' | 'off';

/** The pill, plus the tint the whole row wears behind it. */
export interface AllRowStatus {
  readonly kind: AllRowPillKind;
  /** The pill's words, uppercased by CSS. */
  readonly label: string;
  /**
   * The severity tint for a SURFACED row — the row IS the state, so a firing row
   * tints its whole background. `null` for every other row: a quiet list must
   * stay quiet, or the colour stops meaning anything.
   */
  readonly tint: 'alarm' | 'warning' | 'caution' | null;
}

/** The fields the pill decision reads. */
export interface AllRowStatusInput {
  readonly enabled: boolean;
  /** In the ACTIVE band right now — read from that band, never re-derived. */
  readonly surfaced: boolean;
  readonly compilePending: boolean;
  readonly runtimeDisabledReason?: string;
  readonly severity?: AlarmSeverity;
}

/**
 * Decide one row's pill.
 *
 * Order matters and is deliberate: OFF wins over everything (an owner who
 * switched an alarm off is owed the plainest possible answer), then firing, then
 * not-evaluating, then quiet. A DISABLED definition can still carry a stale
 * runtime reason from before it was switched off — reading that as "stalled"
 * would answer a question nobody asked.
 */
export function allRowStatus(input: AllRowStatusInput): AllRowStatus {
  if (!input.enabled) return { kind: 'off', label: 'Off', tint: null };
  if (input.surfaced) {
    switch (input.severity) {
      case 'warning':
        return { kind: 'warning', label: 'Active', tint: 'warning' };
      case 'caution':
        return { kind: 'caution', label: 'Active', tint: 'caution' };
      default:
        // Emergency, alarm, and an absent severity all read as the loud family:
        // an alarm the boat is sounding with no severity on the wire is not a
        // reason to draw it calmly.
        return { kind: 'alarm', label: 'Active', tint: 'alarm' };
    }
  }
  if (input.compilePending || input.runtimeDisabledReason !== undefined) {
    return { kind: 'stalled', label: 'Not evaluating', tint: null };
  }
  return { kind: 'quiet', label: 'Quiet', tint: null };
}

// ---------------------------------------------------------------------------
// The On/Off filter.
// ---------------------------------------------------------------------------

/** Does this row survive the toolbar's enabled-state filter? */
export function matchesAlarmsFilter(view: { readonly enabled: boolean }, filter: AlarmsFilter) {
  if (filter === 'on') return view.enabled;
  if (filter === 'off') return !view.enabled;
  return true;
}

/** A group section after the filter, carrying what the heading may claim. */
export interface FilteredSection {
  readonly group: ConfiguredAlarmSection['group'];
  /** The rows still visible. Never empty — an emptied section is dropped. */
  readonly rows: ConfiguredAlarmView[];
  /** The dot's tone, from the UNFILTERED group (see the module header). */
  readonly tone: ConfiguredAlarmSection['tone'];
  /**
   * The heading's number: how many alarms this group HAS (after the search,
   * before the On/Off filter). The design pins this to the group total on
   * purpose — a heading that counted down as you filtered would read as alarms
   * disappearing off the boat.
   */
  readonly total: number;
}

/**
 * Apply the enabled-state filter to already-filed sections.
 *
 * A section with no surviving rows is dropped whole — heading and card — rather
 * than left as an empty box with a count in it.
 */
export function filterSections(
  sections: readonly ConfiguredAlarmSection[],
  filter: AlarmsFilter
): FilteredSection[] {
  const out: FilteredSection[] = [];
  for (const section of sections) {
    const rows = section.rows.filter((row) => matchesAlarmsFilter(row, filter));
    if (rows.length === 0) continue;
    out.push({ group: section.group, rows, tone: section.tone, total: section.rows.length });
  }
  return out;
}

/**
 * The "your filter hid everything" line.
 *
 * Deliberately NOT the search-empty wording, which quotes the typed query back:
 * with an empty box and an On/Off chip selected there is no query to quote, and
 * "Nothing matches “”" is how a filter reads as a broken page.
 *
 * `waitingMatches` is how many waiting-for-equipment rows would have matched
 * the filter. The fold below is deliberately never filtered, so those rows are
 * still on screen — and the On chip counts them, because they ARE switched on.
 * Claiming "every alarm aboard is switched off" over a visible fold naming a
 * switched-on alarm is the screen arguing with itself; point at the fold
 * instead.
 */
export function alarmsFilterEmptyText(filter: AlarmsFilter, waitingMatches: number): string {
  if (filter === 'on') {
    return waitingMatches > 0
      ? 'The only alarms switched on are waiting for equipment — see below.'
      : 'Every alarm aboard is switched off.';
  }
  if (filter === 'off') {
    return waitingMatches > 0
      ? 'The only alarms switched off are waiting for equipment — see below.'
      : 'Nothing is switched off — every alarm aboard is on.';
  }
  return 'No alarms to show.';
}

// ---------------------------------------------------------------------------
// The NOW cell's binary line.
// ---------------------------------------------------------------------------

/**
 * The NOW cell for an enabled BINARY row (`alarms_redesign` acceptance:
 * "binary rows show OK/— and time-since-trip when firing").
 *
 * A float switch has no range to read back, so the generic value line ("Now On
 * · alarms when on") answers the wrong question — the owner wants "is it OK,
 * and if not, since when". Firing rows lead with the trip age; quiet rows with
 * a reading earn a plain "OK"; no reading returns `undefined`, which the cell
 * renders as the em-dash — the same honest blank a disabled row gets.
 */
export function binaryNowLine(input: {
  /** In the ACTIVE band right now. */
  readonly surfaced: boolean;
  /** The firing row's last transition stamp, if the page has one. */
  readonly trippedAt: string | null;
  readonly now: number;
  /** Whether a live or stored reading exists (the generic line's own gate). */
  readonly hasReading: boolean;
}): string | undefined {
  if (input.surfaced) {
    const ago = input.trippedAt === null ? null : formatRelativeShort(input.trippedAt, input.now);
    return ago === null ? 'Tripped' : `Tripped ${ago}`;
  }
  return input.hasReading ? 'OK' : undefined;
}

// ---------------------------------------------------------------------------
// The NOW cell's mini-bar.
// ---------------------------------------------------------------------------

/**
 * The mini-bar for one row, or `null` when there is nothing honest to draw.
 *
 * Three ways to get `null`, each a refusal rather than a fallback:
 *
 *   - the species is not `analog` — a float switch has no range to sit within;
 *   - no evidence window came back — the signal catalog carries no min/max, so
 *     without an observed range there is nothing to scale against and a 0..100
 *     domain for volts would be invented (data-mapping trap 6);
 *   - the window holds no points — an empty six hours is not a flat line.
 *
 * Value and bounds both come from the evidence series, so the fill can never
 * contradict the domain. The threshold is the COMPILED set point, which is in
 * the same store domain as the series (ADR 0170); the owner-vocabulary rescale
 * some rows apply to their value TEXT is a single multiplier and cannot move a
 * position expressed as a percentage of the same window.
 */
export function miniBarForRow(input: {
  readonly species: AlarmSpecies;
  readonly series: EvidenceSeries | undefined;
  /** `trigger.setpoint`, store domain, or `null`/`undefined` when there is none. */
  readonly threshold: number | null | undefined;
}): MiniBarModel | null {
  if (input.species !== 'analog') return null;
  const series = input.series;
  if (series === undefined) return null;
  const last = series.points[series.points.length - 1];
  if (last === undefined) return null;
  return miniBarModel({
    min: series.min,
    max: series.max,
    value: last.v,
    threshold: input.threshold ?? null,
  });
}
